import { Alert } from "@swot/base-components";
import { CmtsBackendValidatedForm } from "../CmtsBackendValidatedForm.js";
import { CmtsEnterpriseForm, type CmtsFormField } from "../forms/index.js";
import type { CmtsMasterDataCrudEntityConfig } from "../../core/master-data/cmtsMasterDataCrudRegistry.js";
import type { DropdownOption, EntityRecord, EntitySchema, EntitySchemaField, ValidationResult } from "../../types/api.types.js";

export type CmtsMasterDataDropdownMap = Readonly<Record<string, readonly DropdownOption[]>>;

function fieldTypeOf(field: EntitySchemaField): CmtsFormField["type"] {
  const normalized = String(field.type ?? "text").trim().toLowerCase();

  if (normalized === "textarea" || normalized === "multiline") return "textarea";
  if (normalized === "select" || normalized === "dropdown" || Boolean(field.optionsSource)) return "select";
  if (normalized === "date") return "date";
  if (normalized === "datetime" || normalized === "datetime-local") return "datetime-local";
  if (normalized === "number" || normalized === "integer" || normalized === "decimal") return "number";

  return "text";
}

function dropdownOptionsFor(
  field: EntitySchemaField,
  dropdowns: CmtsMasterDataDropdownMap
): readonly { readonly value: string; readonly label: string }[] | undefined {
  const fieldName = String(field.name ?? "").trim();
  const source = String(field.optionsSource ?? fieldName).trim();
  const options = dropdowns[source] ?? dropdowns[fieldName];

  if (!options) return undefined;

  return options.map((option) => ({
    value: String(option.value ?? option.id ?? option.code ?? ""),
    label: String(option.label ?? option.code ?? option.value ?? ""),
  }));
}

function safeFieldName(label: string, index: number): string {
  const cleaned = label
    .replace(/\s+/g, "")
    .replace(/[^A-Za-z0-9_]/g, "")
    .replace(/^./, (char) => char.toLowerCase());

  return cleaned || `field_${index + 1}`;
}

function toFormFields(schema: EntitySchema, dropdowns: CmtsMasterDataDropdownMap): readonly CmtsFormField[] {
  const schemaFields = Array.isArray(schema.fields) ? schema.fields : [];

  return schemaFields
    .filter((field) => {
      const name = String(field.name ?? "").trim();
      return !field.readOnly && name !== "id" && name !== "tenantId" && name !== "masterType" && name !== "group";
    })
    .map((field, index): CmtsFormField => {
      const rawName = String(field.name ?? "").trim();
      const label = String(field.label ?? field.name ?? `Field ${index + 1}`).trim();
      const name = rawName || safeFieldName(label, index);

      const formField: CmtsFormField = {
        name,
        label,
        type: fieldTypeOf(field) ?? "text",
        required: Boolean(field.required),
      };

      const options = dropdownOptionsFor(field, dropdowns);
      if (options) formField.options = options;
      if (field.required) formField.placeholder = `Enter ${label}`;
      if (field.optionsSource) formField.help = `Loaded from backend ${String(field.optionsSource)} dropdown options.`;

      return formField;
    });
}

export function CmtsMasterDataForm(props: {
  readonly config: CmtsMasterDataCrudEntityConfig;
  readonly schema: EntitySchema;
  readonly dropdowns: CmtsMasterDataDropdownMap;
  readonly initialValues?: EntityRecord | null | undefined;
  readonly validation?: ValidationResult | undefined;
  readonly busy?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly onSubmit: (payload: Record<string, unknown>) => void | Promise<void>;
  readonly onCancel: () => void;
}) {
  const fields = toFormFields(props.schema, props.dropdowns);

  return (
    <CmtsBackendValidatedForm validation={props.validation}>
      {props.schema.version ? (
        <Alert title="Backend schema" tone="info">
          Schema version {props.schema.version} is driving the editable fields.
        </Alert>
      ) : null}

      <CmtsEnterpriseForm
        fields={fields}
        initialValues={props.initialValues ?? { active: true, status: "Active", masterType: props.config.masterType }}
        busy={Boolean(props.busy)}
        error={props.error ?? null}
        submitLabel={props.initialValues ? "Save changes" : `Create ${props.config.singularTitle}`}
        cancelLabel="Cancel"
        onSubmit={props.onSubmit}
        onCancel={props.onCancel}
      />
    </CmtsBackendValidatedForm>
  );
}